import { useState, useEffect, useMemo } from "react";
import type { ProductVariantDto } from "../../../../api";
import { ProductImage } from "./product-image";
import { ProductHeader } from "./product-header";
import { ProductVariantSelector } from "./product-variant-selector";
import { ProductDetailsGrid } from "./product-details-grid";
import { ProductActions } from "./product-actions";
import { useMePartner } from "../../../../swr";
import { useMe } from "../../../../swr";
import { Role } from "../../../../api";
import { ProductPricingTiers } from "./product-pricing-tiers";
import { formatDisplayName } from "../../utils/format-display-name";
import { Text } from "../../../../components/common/typography";
import { ProductVariantSpecs } from "./product-variant-specs";
import { DatasheetSection } from "./datasheet-section";

type ProductDetailsProduct = ProductVariantDto & {
  description?: string | null;
};

interface ProductDetailsContentProps {
  products: ProductVariantDto[];
  baseProduct: ProductDetailsProduct;
  showActions?: boolean;
  enableStockUpdate?: boolean;
  productId?: number;
}

export function ProductDetailsContent({
  products,
  baseProduct,
  showActions = true,
  enableStockUpdate = false,
  productId,
}: ProductDetailsContentProps) {
  const { user } = useMe();
  const { isBuyer } = useMePartner();
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(
    null
  );
  const [quantity, setQuantity] = useState(1);

  // Only show actions (Add to Cart + Quantity) for buyers (not admins)
  const shouldShowActions = showActions && isBuyer && user?.role !== Role.Admin;

  // Get selected variant product
  const selectedVariant = useMemo(() => {
    if (!products || !selectedVariantId) return null;
    return (
      products.find((p: ProductVariantDto) => p.id === selectedVariantId) ||
      products[0]
    );
  }, [products, selectedVariantId]);

  // Current product to display (selected variant or base)
  const currentProduct = (selectedVariant ||
    baseProduct) as ProductDetailsProduct;

  const pricingTiers = currentProduct?.pricelistItems?.length
    ? currentProduct.pricelistItems
    : baseProduct.pricelistItems;

  const activeTier = useMemo(() => {
    if (!pricingTiers || pricingTiers.length === 0) {
      return null;
    }

    const sortedTiers = [...pricingTiers].sort(
      (a, b) => (a.minQuantity ?? 0) - (b.minQuantity ?? 0)
    );
    const targetQty = quantity > 0 ? quantity : 1;

    let matchedTier = sortedTiers[0];
    for (const tier of sortedTiers) {
      const minQty = tier.minQuantity ?? 1;
      if (targetQty >= minQty) {
        matchedTier = tier;
      } else {
        break;
      }
    }

    return matchedTier;
  }, [pricingTiers, quantity]);

  // Calculate current price prioritizing active pricelist tier before fallback pricing
  const currentPrice = useMemo(() => {
    if (!currentProduct) return 0;

    if (
      activeTier &&
      typeof activeTier.fixedPrice === "number" &&
      !Number.isNaN(activeTier.fixedPrice)
    ) {
      return activeTier.fixedPrice;
    }

    const listPrice = currentProduct.listPrice ?? baseProduct.listPrice ?? 0;
    const priceExtra = currentProduct.priceExtra ?? 0;
    return listPrice + priceExtra;
  }, [activeTier, baseProduct.listPrice, currentProduct]);

  const availableQuantity = useMemo(() => {
    if (!currentProduct) return 0;
    return Number(currentProduct.supplierStock ?? 0);
  }, [currentProduct]);

  // Auto-select first variant if products exist and none selected
  useEffect(() => {
    if (products && products.length > 0 && selectedVariantId === null) {
      const firstAvailableProduct = products.find(
        (p: ProductVariantDto) => (p.supplierStock ?? 0) > 0
      );
      if (firstAvailableProduct) {
        setSelectedVariantId(firstAvailableProduct.id);
      } else if (products.length > 0) {
        setSelectedVariantId(products[0].id);
      }
    }
  }, [products, selectedVariantId]);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      {/* Left Side - Product Image */}
      <ProductImage
        key={currentProduct?.id || baseProduct.id}
        currentProduct={currentProduct}
        baseProduct={baseProduct}
      />

      {/* Right Side - Product Details */}
      <div className="flex-1 flex flex-col gap-6">
        <ProductHeader
          productName={
            formatDisplayName(currentProduct?.displayName) ||
            currentProduct?.name ||
            formatDisplayName(baseProduct.displayName) ||
            baseProduct.name ||
            "—"
          }
          price={currentPrice}
        />

        {(currentProduct?.description || baseProduct.description) && (
          <Text variant="body" tone="body">
            {currentProduct?.description || baseProduct.description}
          </Text>
        )}

        {/* Variant Selector */}
        {products && (products.length > 1 || enableStockUpdate) && (
          <ProductVariantSelector
            variants={products}
            selectedVariantId={selectedVariantId}
            onVariantChange={setSelectedVariantId}
            enableStockUpdate={enableStockUpdate}
            productId={productId}
          />
        )}

        {/* Actions (Add to Cart + Quantity) - Only for buyers */}
        {shouldShowActions && (
          <ProductActions
            product={currentProduct}
            quantity={quantity}
            onQuantityChange={setQuantity}
            availableQuantity={availableQuantity}
          />
        )}

        <ProductPricingTiers tiers={pricingTiers} activeQuantity={quantity} />

        <ProductVariantSpecs product={currentProduct} />

        <DatasheetSection
          productTemplateId={currentProduct.productTemplateId ?? baseProduct.productTemplateId ?? null}
          hasDatasheetPdf={currentProduct.hasDatasheetPdf ?? baseProduct.hasDatasheetPdf ?? false}
          externalDatasheetUrl={currentProduct.datasheetUrl ?? baseProduct.datasheetUrl ?? null}
        />

        {/* Product Details Section */}
        <ProductDetailsGrid
          baseProduct={baseProduct}
          currentProduct={currentProduct || baseProduct}
          selectedVariant={selectedVariant}
          availableQuantity={availableQuantity}
        />
      </div>
    </div>
  );
}
